import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { isOffline } from '../lib/api'
import { getCurrentFix } from '../lib/geo'
import { enqueue, newClientId } from '../lib/outbox'
import { useSession } from '../state/session'
import { useToday } from '../state/today'
import { useToast } from '../state/toast'
import { PhotoPicker, type AttachedPhoto } from '../components/PhotoPicker'
import { SiteSelect } from '../components/AddSiteSheet'
import { Button, ErrorNote, Field, Spinner } from '../components/ui'

export default function NewNotePage() {
  const { state, refresh, clockOut } = useToday()
  const { settings } = useSession()
  const toast = useToast()
  const navigate = useNavigate()
  const [params] = useSearchParams()

  const scheduleId = params.get('schedule')
  const then = params.get('then')

  const prompt = useMemo(
    () => state?.prompts.find((p) => p.scheduleId === scheduleId) ?? null,
    [state, scheduleId],
  )

  const [type, setType] = useState('status_update')
  const [body, setBody] = useState('')
  const [siteId, setSiteId] = useState('')
  const [photos, setPhotos] = useState<AttachedPhoto[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Lunch: the reminder offers a clock-out alongside the update. Optional —
  // plenty of people eat on site with the clock running. Tapping "Clock out
  // for lunch" on the notification arrives with it already ticked.
  const [lunchOut, setLunchOut] = useState(params.get('lunch') === '1')

  useEffect(() => {
    if (!state) return
    setSiteId((current) => current || state.openEntry?.siteId || '')
    if (prompt?.noteType) setType(prompt.noteType)
  }, [state, prompt])

  if (!state) {
    return (
      <div className="py-16">
        <Spinner />
      </div>
    )
  }

  // A photo is encouraged on a scheduled update, never required. Somebody in a
  // dark crawlspace with one bar of signal still needs to be able to say what is
  // going on, and blocking them just means no update at all.
  const photoEncouraged = settings.encouragePhoto && Boolean(prompt) && photos.length === 0
  const offerLunch = Boolean(prompt?.offerClockOut && state.openEntry) && then !== 'clock-out'
  const canSubmit = body.trim().length > 0 || photos.length > 0

  const submit = async () => {
    setBusy(true)
    setError(null)

    const clientId = newClientId()
    const occurredAt = new Date().toISOString()

    let fix: { lat: number; lng: number; accuracy: number } | null = null
    try {
      fix = await getCurrentFix({ timeout: 6000 })
    } catch {
      // Location is a nice-to-have on a note; the photo is the evidence.
    }

    const payload = {
      type,
      body: body.trim() || undefined,
      siteId: siteId || null,
      promptKey: prompt?.key ?? null,
      scheduleId: prompt?.scheduleId ?? null,
      occurredAt,
      clientId,
      lat: fix?.lat,
      lng: fix?.lng,
      accuracy: fix?.accuracy,
    }

    const form = new FormData()
    form.set('payload', JSON.stringify(payload))
    photos.forEach((photo, index) => {
      form.set(`photo_${index}`, new File([photo.full], `photo-${index}.jpg`, { type: 'image/jpeg' }))
      form.set(`thumb_${index}`, new File([photo.thumb], `thumb-${index}.jpg`, { type: 'image/jpeg' }))
      form.set(`dims_${index}`, `${photo.width}x${photo.height}`)
    })

    try {
      const res = await fetch('/api/notes', {
        method: 'POST',
        body: form,
        credentials: 'same-origin',
      })
      if (!res.ok) {
        const detail = (await res.json().catch(() => null)) as { error?: string } | null
        throw new Error(detail?.error ?? `Could not post the update (${res.status})`)
      }
      await finish('Update posted')
    } catch (err) {
      if (isOffline(err)) {
        await enqueue({
          kind: 'note',
          url: '/api/notes',
          method: 'POST',
          payload,
          files: photos.flatMap((photo, index) => [
            { field: `photo_${index}`, blob: photo.full, filename: `photo-${index}.jpg` },
            { field: `thumb_${index}`, blob: photo.thumb, filename: `thumb-${index}.jpg` },
          ]),
          summary: `${prompt?.label ?? 'Update'}${photos.length ? ` · ${photos.length} photo${photos.length > 1 ? 's' : ''}` : ''}`,
        })
        await finish('Saved offline. It will send when you get signal.')
      } else {
        setError(err instanceof Error ? err.message : 'Could not post the update')
        setBusy(false)
      }
    }
  }

  async function clockOutForLunch() {
    try {
      await clockOut({ lunch: true, scheduleId: prompt?.scheduleId })
      toast.success('Clocked out for lunch. You will get a reminder to clock back in.')
    } catch {
      toast.warn('The lunch clock-out did not go through. Clock out from the home screen.')
    }
  }

  async function finish(message: string) {
    toast.success(message)
    await refresh()
    if (offerLunch && lunchOut) await clockOutForLunch()
    if (then === 'clock-out') {
      try {
        await clockOut()
      } catch {
        toast.warn('Update posted, but the clock-out did not go through.')
      }
    }
    navigate('/', { replace: true })
  }

  return (
    <div className="space-y-5">
      <header>
        <p className="label">{prompt ? prompt.label : 'New update'}</p>
        <h1 className="mt-0.5 text-2xl">
          {prompt ? prompt.title : 'What is happening on site?'}
        </h1>
        {prompt && <p className="mt-1 text-sm text-graphite-2">{prompt.body}</p>}
      </header>

      <PhotoPicker
        photos={photos}
        onChange={setPhotos}
        maxDimension={settings.photo.maxDimension}
        quality={settings.photo.quality}
      />
      {photoEncouraged && (
        <p className="text-xs text-graphite-2">
          A photo is worth more than a paragraph here, but text on its own is fine.
        </p>
      )}

      <Field label="Type">
        <select className="field" value={type} onChange={(e) => setType(e.target.value)}>
          {state.noteTypes.map((noteType) => (
            <option key={noteType.key} value={noteType.key}>
              {noteType.label}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Site" hint={state.openEntry ? 'Defaults to where you are clocked in' : undefined}>
        <SiteSelect value={siteId} onChange={setSiteId} sites={state.sites} emptyLabel="No site" />
      </Field>

      <Field label="Notes" hint="What got done, what is in the way, what you need.">
        <textarea
          className="field"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Second floor framing is up. Waiting on the window delivery for the east wall."
        />
      </Field>

      {offerLunch && (
        <label className="card flex min-h-12 items-center gap-3 p-3">
          <input
            type="checkbox"
            className="size-5 shrink-0 accent-graphite"
            checked={lunchOut}
            onChange={(e) => setLunchOut(e.target.checked)}
          />
          <span className="text-sm">
            <span className="font-bold">Clock out for lunch</span>
            <span className="block text-xs text-graphite-2">
              Optional. You get a reminder to clock back in.
            </span>
          </span>
        </label>
      )}

      <ErrorNote>{error}</ErrorNote>

      <div className="flex gap-2">
        <Button className="flex-1" onClick={() => navigate(-1)} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="primary"
          className="flex-[2]"
          loading={busy}
          disabled={!canSubmit}
          onClick={() => void submit()}
        >
          {then === 'clock-out'
            ? 'Post and clock out'
            // Naming the omission keeps it a conscious choice rather than a
            // blocked one — the worker can still post in one tap.
            : offerLunch && lunchOut ? 'Post and go to lunch'
              : photoEncouraged ? 'Post without a photo' : 'Post update'}
        </Button>
      </div>

      {offerLunch && (
        <Button
          variant="ghost"
          className="w-full"
          disabled={busy}
          onClick={() => {
            setBusy(true)
            void clockOutForLunch().then(() => navigate('/', { replace: true }))
          }}
        >
          Skip the update, just clock out for lunch
        </Button>
      )}
    </div>
  )
}
